import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import InfiniteScroll from "react-infinite-scroll-component";
import {
  getChatList,
  getChatMessages,
  setSelectedChat,
  updateChatList,
  markConversationAsRead,
} from "@/store/slices/chatSlice";
import { DocumentIcon, MagnifyingGlassIcon, PaperClipIcon, PhotoIcon } from "@heroicons/react/24/outline";
import { useSocket } from "../../components/SocketContext";
import { UserGroupIcon, UserPlusIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { playNotificationSound } from "@/utils/notificationSound";
import { useTranslation } from "react-i18next";

export function ChatList({ onCreateGroup, onSingleChatCreate }) {
  const socket = useSocket();
  const dispatch = useDispatch();

  const { t } = useTranslation('chat');
  const { user } = useSelector((state) => state.user || {});
  const { chat_list = [], chat_list_pagination, selectedChat } = useSelector((state) => state.chat || {});

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");

  /* --------------------------------
     Initial chat list load
  -------------------------------- */
  useEffect(() => {
    dispatch(getChatList({ page: 1, search: search, filter: filter }));
  }, [dispatch, search, filter]);

  /* --------------------------------
     Load more chat_list
  -------------------------------- */
  const fetchMoreData = () => {
    if (chat_list_pagination?.has_more) {
      dispatch(
        getChatList({
          page: chat_list_pagination.current_page + 1,
        })
      );
    }
  };

  useEffect(() => {
  if (!socket) return;
  const handleReceiveMessage = (message) => {
    const msgData = message?.data || message;
    if (msgData?.sender_id != null && Number(msgData.sender_id) !== Number(user?.id)) {
      playNotificationSound();
    }
    dispatch((dispatch, getState) => {
      const { chat_list, selectedChat } = getState().chat;
      const chatIndex = chat_list.findIndex(
        (c) => c.conversation_id === message?.data?.conversation_id
      );
      if (chatIndex > -1) {
        const isViewingThisChat = selectedChat?.conversation_id === message?.data?.conversation_id;
        const updatedChat = {
          ...chat_list[chatIndex],
          last_message: message?.data,
          unread_count: isViewingThisChat ? 0 : (message?.data?.unread_count ?? chat_list[chatIndex].unread_count),
        };
        const newChatList = [...chat_list];
        newChatList.splice(chatIndex, 1); 
        newChatList.unshift(updatedChat); 
        dispatch(updateChatList(newChatList)); 
      } else {
        dispatch(getChatList({ page: 1, search, filter }));
      }
    });
  };

  socket.on("receive_message", handleReceiveMessage);
  return () => {
    socket.off("receive_message", handleReceiveMessage);
  };
}, [socket, dispatch, search, filter, user?.id]);

  /* --------------------------------
     Chat click handler
  -------------------------------- */
  const handleChatClick = (chat) => {
    dispatch(markConversationAsRead(chat.conversation_id));
    dispatch(setSelectedChat(chat));
    dispatch(
      getChatMessages({
        id: chat.conversation_id,
        page: 1,
      })
    );
    dispatch(getChatList({ page: 1, search, filter }));
    if (socket?.connected) {
      if (chat?.type === "group") {
        socket.emit("join_group", chat.chat_with?.id);
      } else {
        socket.emit("send_seen_event", {
          receiver_id: chat.chat_with?.id,
          conversation_id: chat.conversation_id,
        });
      }
    }
  };

 const formatMessageTime = (updated_at) => {
  if (!updated_at) return "";
  const date = new Date(updated_at);
  const now = new Date();

  const diffMs = now - date; // difference in milliseconds
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffMonths = now.getMonth() - date.getMonth() + 12 * (now.getFullYear() - date.getFullYear());
  const diffYears = now.getFullYear() - date.getFullYear();

  const timeOptions = { hour: "2-digit", minute: "2-digit", hour12: false };
  const formattedTime = date.toLocaleTimeString([], timeOptions);

  if (diffDays === 0) return formattedTime; // Today → show time
  if (diffDays === 1) return `${t('yesterday')} ${formattedTime}`;
  if (diffDays < 30) return t('daysAgo', { count: diffDays });
  if (diffMonths < 12) return t('monthsAgo', { count: diffMonths });
  return t('yearsAgo', { count: diffYears });
};



  const truncate = (text, limit = 20) => {
  if (!text) return "";
  return text?.length > limit ? text.slice(0, limit) + "..." : text;
};

  /* --------------------------------
     Sidebar Item Component
  -------------------------------- */
  const SidebarItem = ({ chat }) => {
    const { chat_with, last_message, updated_at, unread_count } = chat;
    const time = updated_at ? formatMessageTime(updated_at) : "";

   
    const isActive = selectedChat?.conversation_id === chat.conversation_id;

    return (
      <div
        onClick={() => handleChatClick(chat)}
        className={`flex items-start p-2.5 border-b border-gray-100 cursor-pointer transition-all duration-200
          ${isActive 
            ? "bg-gradient-to-r from-[#065C36]/10 to-transparent border-l-4 border-l-[#065C36]" 
            : "hover:bg-gray-50 active:bg-gray-100"
          }`}
      >
        <div className="mr-2.5 shrink-0 relative">
          {chat_with?.image ? (
            <div className="w-10 h-10 rounded-full overflow-hidden ring-2 ring-offset-2 ring-offset-white ring-gray-100">
              <img
                src={chat_with.image}
                alt={chat_with.name}
                className="w-full h-full object-cover"
              />
            </div>
          ) : (
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#065C36] to-[#044a2b] flex items-center justify-center text-xs font-bold text-white ring-2 ring-offset-2 ring-offset-white ring-gray-100">
              {chat_with?.name?.substring(0, 2).toUpperCase()}
            </div>
          )}
          {unread_count > 0 && (
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[9px] rounded-full flex items-center justify-center font-bold ring-2 ring-white">
              {unread_count > 9 ? '9+' : unread_count}
            </span>
          )}
        </div>

        <div className="flex-grow min-w-0">
          <div className="flex justify-between items-start mb-1">
            <h4 className={`text-xs font-semibold truncate ${isActive ? 'text-[#065C36]' : 'text-gray-900'}`}>
              {chat_with?.name}
            </h4>
            <span className="text-[10px] text-gray-400 shrink-0 ml-2">{time}</span>
          </div>

          <div className="flex items-center justify-between gap-2">
            <p className="text-[11px] text-gray-600 truncate flex items-center gap-1 flex-1">
              {last_message ? (
                last_message.type === "image" ? (
                  <><span className="text-sm">📷</span><span>{t('image')}</span></>
                ) : last_message.type === "file" ? (
                  <><span className="text-sm">📄</span><span>{t('document')}</span></>
                ) : (
                  truncate(last_message?.message, 30) 
                )
              ) : (
                <span className="text-gray-400 italic text-[10px]">{t('noMessageYet')}</span>
              )}
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full bg-white relative">
      {/* Search */}
      <div className="p-3 pb-2 border-b border-gray-100">
        <div className="relative">
          <MagnifyingGlassIcon className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('searchConversations')}
            className="w-full bg-gray-50 rounded-lg py-2 pl-8 pr-8 text-xs focus:outline-none focus:ring-2 focus:ring-[#065C36]/20 focus:bg-white border border-transparent focus:border-[#065C36]/30 transition-all placeholder-gray-400"
          />
          {search && (
            <XMarkIcon 
              className="w-3.5 h-3.5 absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer hover:text-gray-600 transition-colors" 
              onClick={() => setSearch('')} 
            />
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 px-3 pt-2 pb-1.5">
        {["all", "unread"].map((r) => (
          <button
            key={r}
            onClick={() => setFilter(r)}
            className={`px-3 py-1 rounded-md text-[10px] font-medium transition-all duration-200
              ${filter === r
              ? "bg-[#065C36] text-white shadow-sm"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
          >
            {r === "all" ? `${t('all')}` : `${t('unread')}`}
          </button>
        ))}
      </div>

      {/* Chat List */}
      <div id="scrollableDiv" className="flex-grow overflow-y-auto custom-scrollbar">
        {chat_list?.length === 0 && !chat_list_pagination?.has_more ? (
          <div className="flex flex-col items-center justify-center h-full px-4 py-12">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <p className="text-gray-500 text-sm font-medium">{t('noConversations')}</p>
            <p className="text-gray-400 text-xs mt-1">{t('startNewChat')}</p>
          </div>
        ) : (
          <InfiniteScroll
            dataLength={chat_list?.length || 0}
            next={fetchMoreData}
            hasMore={chat_list_pagination?.has_more || false}
            loader={
              <div className="flex justify-center mb-4 mt-4">
                <div className="h-6 w-6 border-2 border-gray-300 border-t-[#065C36] rounded-full animate-spin"></div>
              </div>
            }
            scrollableTarget="scrollableDiv"
            endMessage={
              chat_list?.length > 0 && (
                <p className="text-center text-gray-400 py-4 text-xs">
                  {t('endOfConversations')}
                </p>
              )
            }
          >
            {chat_list?.length > 0 && chat_list.map((chat) => (
              <SidebarItem
                key={chat.conversation_id}
                chat={chat}
              />
            ))}
          </InfiniteScroll>
        )}
      </div>

      {/* Floating Action Buttons */}
      <div className="absolute bottom-3 right-3 flex flex-col gap-2">
        <button
          onClick={onSingleChatCreate}
          className="w-12 h-12 bg-[#065C36] text-white rounded-full shadow-lg flex items-center justify-center hover:bg-[#044a2b] transition-all duration-200 hover:scale-105 active:scale-95 group"
          title={t('newChat')}
        >
          <UserPlusIcon className="w-4 h-4 text-white" />
        </button>
        <button
          onClick={onCreateGroup}
          className="w-12 h-12 bg-[#065C36] text-white rounded-full shadow-lg flex items-center justify-center hover:bg-[#044a2b] transition-all duration-200 hover:scale-105 active:scale-95 group"
          title={t('newGroup')}
        >
          <UserGroupIcon className="w-4 h-4 text-white" />
        </button>
      </div>
    </div>
  );
}

export default ChatList;